"use client"

import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";
import { useIsMobile } from "@/shared/hooks/use-mobile";

/**
 * Responsive Drawer:
 * - Mobile (< md): bottom sheet via vaul.
 * - Tablet & Desktop (md+): centered modal via Radix Dialog.
 *
 * Same exported API as before — call sites don't need to change.
 */

type Mode = "drawer" | "dialog";
const ModeContext = React.createContext<Mode>("drawer");

const Drawer = ({
  shouldScaleBackground = true,
  open,
  onOpenChange,
  defaultOpen,
  modal,
  children,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => {
  const isMobile = useIsMobile();
  const mode: Mode = isMobile ? "drawer" : "dialog";

  if (mode === "dialog") {
    return (
      <ModeContext.Provider value="dialog">
        <DialogPrimitive.Root
          open={open}
          onOpenChange={onOpenChange}
          defaultOpen={defaultOpen}
          modal={modal}
        >
          {children}
        </DialogPrimitive.Root>
      </ModeContext.Provider>
    );
  }

  return (
    <ModeContext.Provider value="drawer">
      <DrawerPrimitive.Root
        shouldScaleBackground={shouldScaleBackground}
        open={open}
        onOpenChange={onOpenChange}
        defaultOpen={defaultOpen}
        modal={modal}
        {...props}
      >
        {children}
      </DrawerPrimitive.Root>
    </ModeContext.Provider>
  );
};
Drawer.displayName = "Drawer";

const DrawerTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Trigger>
>((props, ref) => {
  const mode = React.useContext(ModeContext);
  if (mode === "dialog") {
    return <DialogPrimitive.Trigger ref={ref} {...(props as any)} />;
  }
  return <DrawerPrimitive.Trigger ref={ref} {...props} />;
});
DrawerTrigger.displayName = "DrawerTrigger";

const DrawerPortal = ({ children, ...props }: React.ComponentProps<typeof DrawerPrimitive.Portal>) => {
  const mode = React.useContext(ModeContext);
  if (mode === "dialog") {
    return <DialogPrimitive.Portal {...(props as any)}>{children}</DialogPrimitive.Portal>;
  }
  return <DrawerPrimitive.Portal {...props}>{children}</DrawerPrimitive.Portal>;
};

const DrawerClose = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Close>
>((props, ref) => {
  const mode = React.useContext(ModeContext);
  if (mode === "dialog") {
    return <DialogPrimitive.Close ref={ref} {...(props as any)} />;
  }
  return <DrawerPrimitive.Close ref={ref} {...props} />;
});
DrawerClose.displayName = "DrawerClose";

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  const mode = React.useContext(ModeContext);
  if (mode === "dialog") {
    return (
      <DialogPrimitive.Overlay
        ref={ref as any}
        className={cn(
          "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          className,
        )}
        {...(props as any)}
      />
    );
  }
  return (
    <DrawerPrimitive.Overlay ref={ref} className={cn("fixed inset-0 z-50 bg-black/80", className)} {...props} />
  );
});
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  const mode = React.useContext(ModeContext);

  if (mode === "dialog") {
    return (
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          ref={ref as any}
          className={cn(
            "fixed left-[50%] top-[50%] z-50 flex max-h-[85vh] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] flex-col gap-2 overflow-hidden rounded-lg border bg-background shadow-lg",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            className,
          )}
          {...(props as any)}
        >
          {children}
          <DialogPrimitive.Close className="absolute right-3 top-3 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    );
  }

  return (
    <DrawerPrimitive.Portal>
      <DrawerPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80" />
      <DrawerPrimitive.Content
        ref={ref}
        className={cn(
          "fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto flex-col rounded-t-[10px] border bg-background",
          className,
        )}
        {...props}
      >
        <div className="mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" />
        {children}
      </DrawerPrimitive.Content>
    </DrawerPrimitive.Portal>
  );
});
DrawerContent.displayName = "DrawerContent";

const DrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("grid gap-1.5 p-4 text-center sm:text-left", className)} {...props} />
);
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("mt-auto flex flex-col gap-2 p-4", className)} {...props} />
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => {
  const mode = React.useContext(ModeContext);
  if (mode === "dialog") {
    return (
      <DialogPrimitive.Title
        ref={ref as any}
        className={cn("text-lg font-semibold leading-none tracking-tight", className)}
        {...(props as any)}
      />
    );
  }
  return (
    <DrawerPrimitive.Title
      ref={ref}
      className={cn("text-lg font-semibold leading-none tracking-tight", className)}
      {...props}
    />
  );
});
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => {
  const mode = React.useContext(ModeContext);
  if (mode === "dialog") {
    return (
      <DialogPrimitive.Description
        ref={ref as any}
        className={cn("text-sm text-muted-foreground", className)}
        {...(props as any)}
      />
    );
  }
  return (
    <DrawerPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
  );
});
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};